import React, { JSX, memo, useCallback, useMemo, useRef, useState } from 'react';
import {
    Box,
    IconButton,
    iconButtonClasses,
    ListItemText,
    Menu,
    MenuItem,
    Stack,
    Theme,
    useTheme,
} from '@mui/material';
import { SystemProps } from '@mui/system';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import NoneIcon from '@mui/icons-material/Block';
import NeitherIcon from '@mui/icons-material/HighlightOff';
import UnspecifiedIcon from '@mui/icons-material/RadioButtonUnchecked';
import PsychologyAltIcon from '@mui/icons-material/PsychologyAlt';
import { SpecialValue, SpecialValues } from '@tymely/atoms';

const IconMap: Record<SpecialValue, JSX.Element> = {
    neither: <NeitherIcon />,
    none: <NoneIcon />,
    unspecified: <UnspecifiedIcon />,
    unknown: <PsychologyAltIcon />,
};

export const SpecialValuesSelect = memo(
    (
        props: SystemProps<Theme> & {
            values: SpecialValue[];
            options?: SpecialValue[];
            disabled?: boolean;
            onChange?: (values: SpecialValue[]) => void;
        },
    ) => {
        const theme = useTheme();
        const [open, setOpen] = useState(false);
        const boxRef = useRef<HTMLDivElement>(null);

        const options = useMemo(
            () =>
                (props.options || SpecialValues).map((value) => ({
                    value,
                    icon: IconMap[value],
                })),
            [props.options],
        );

        const { values, onChange, ...rest } = props;

        const handleSelect = useCallback(
            (value: SpecialValue) => {
                const nextValues = values.includes(value) ? values.filter((sp) => sp !== value) : values.concat(value);
                onChange?.(nextValues);
                //  Closing when all options are selected
                if (nextValues.length === options.length) setOpen(false);
            },
            [values, options, onChange],
        );

        const selectedValues = useMemo(() => values.sort(), [values]);

        return (
            <Box sx={{ height: theme.spacing(5) }} ref={boxRef} {...rest}>
                <Box height="100%" display="flex" justifyContent="center">
                    {
                        <Stack justifyContent="center">
                            {selectedValues.map((value) =>
                                React.cloneElement(IconMap[value], {
                                    key: value,
                                    sx: {
                                        color: props.disabled
                                            ? theme.palette.text.disabled
                                            : theme.palette.text.primary,
                                        fontSize: '1em',
                                        cursor: 'pointer',
                                        height: '100%',
                                        borderRadius: '4px 10px 10px 4px',
                                        padding: '4px',
                                        '&:hover': {
                                            backgroundColor: props.disabled ? 'transparent' : theme.palette.grey[300],
                                        },
                                    },
                                    onClick: () => setOpen(true),
                                }),
                            )}
                        </Stack>
                    }
                    {!selectedValues.length && (
                        <IconButton
                            sx={{
                                p: 0.5,
                                [`&.${iconButtonClasses.root}`]: {
                                    borderRadius: '4px 10px 10px 4px',
                                    '&:hover': {
                                        backgroundColor: props.disabled ? 'transparent' : theme.palette.grey[200],
                                    },
                                },
                            }}
                            disabled={props.disabled}
                            onClick={() => setOpen(true)}
                        >
                            <MoreVertIcon fontSize="small" />
                        </IconButton>
                    )}
                </Box>
                <Menu anchorEl={boxRef.current} open={open} onClose={() => setOpen(false)}>
                    {options.map((option) => (
                        <MenuItem
                            key={option.value}
                            value={option.value}
                            selected={props.values.includes(option.value)}
                            onClick={() => handleSelect(option.value)}
                        >
                            {React.cloneElement(IconMap[option.value], {
                                sx: {
                                    color: theme.palette.text.secondary,
                                    fontSize: '1.25em',
                                    mr: 1,
                                },
                            })}
                            <ListItemText
                                sx={{ textTransform: 'capitalize' }}
                                primary={option.value === 'none' ? 'missing' : option.value}
                            />
                        </MenuItem>
                    ))}
                </Menu>
            </Box>
        );
    },
);

SpecialValuesSelect.displayName = 'SpecialValuesSelect';
